import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../contexts/AuthContext';
import { getLoanById, reviewLoanDocument } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

const statusPalette = {
  'Under Processing': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'Resubmission Required': 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  Approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Disbursed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  Hold: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

const reasonOptions = [
  { value: 'aadhaar_blurry', label: 'Aadhaar blurry' },
  { value: 'aadhaar_mismatch', label: 'Aadhaar mismatch' },
  { value: 'pan_invalid', label: 'PAN invalid' },
  { value: 'pan_name_mismatch', label: 'PAN name mismatch' },
  { value: 'document_expired', label: 'Document expired' },
  { value: 'unreadable', label: 'Unreadable' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'other', label: 'Other' },
];

const loanTypeLabelMap = {
  home: 'Home Loan',
  business: 'Business Loan',
  personal: 'Personal Loan',
  car: 'Car Loan',
  education: 'Education Loan',
  other: 'Other',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusPalette[status] || 'bg-slate-100 text-slate-700'}`}>
    {status}
  </span>
);

const fmt = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
}).format(Number(n || 0));

export function LoanReviewDetails({
  loan,
  loanId,
  onUpdated,
  showFooter = true,
  onClose,
}) {
  const { token, user } = useAuth();
  const [details, setDetails] = useState(loan || null);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reviewForm, setReviewForm] = useState({ reason_code: '', note: '' });
  const effectiveLoanId = loanId || loan?.id;

  const canReview = user?.role === 'super_admin' || user?.role === 'wl_admin' || user?.role === 'employee';

  useEffect(() => {
    setDetails(loan || null);
  }, [loan]);

  useEffect(() => {
    if (!effectiveLoanId || !token) return;
    let ignore = false;

    const load = async () => {
      setLoading(true);
      try {
        const resp = await getLoanById(token, effectiveLoanId);
        if (!ignore) {
          setDetails(resp?.data || loan);
        }
      } catch (err) {
        if (!ignore) {
          toast.error(err?.message || 'Failed to load loan details');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [effectiveLoanId, loan, token]);

  if (!effectiveLoanId && !loan) {
    return (
      <div className="rounded-xl border border-dashed px-4 py-6 text-sm text-muted-foreground">
        No loan selected.
      </div>
    );
  }

  const resolvedLoan = details || loan;
  if (!resolvedLoan) {
    return (
      <div className="rounded-xl border border-dashed px-4 py-6 text-sm text-muted-foreground">
        {loading ? 'Loading loan details...' : 'Loan details not found.'}
      </div>
    );
  }

  const productDetails = resolvedLoan.product_details || resolvedLoan.productDetails || {};
  const applicantName = resolvedLoan.applicant_name || resolvedLoan.applicantName || '-';
  const loanType = loanTypeLabelMap[resolvedLoan.loan_type] || resolvedLoan.loanType || resolvedLoan.loan_type || '-';
  const bankName = resolvedLoan.bank_name || resolvedLoan.bank || '-';
  const loanAmount = resolvedLoan.loan_amount ?? resolvedLoan.amount ?? 0;
  const appliedDate = resolvedLoan.created_at || resolvedLoan.appliedDate || '-';
  const documents = Array.isArray(resolvedLoan.documents)
    ? resolvedLoan.documents
    : Array.isArray(productDetails.documents)
      ? productDetails.documents
      : [];

  const submitReview = async (documentId, decision) => {
    if (!token || !effectiveLoanId) return;
    if (decision === 'rejected' && !reviewForm.reason_code) {
      toast.error('Reason code is required when rejecting a document.');
      return;
    }

    setReviewingId(documentId);
    try {
      const resp = await reviewLoanDocument(token, effectiveLoanId, documentId, {
        decision,
        reason_code: decision === 'rejected' ? reviewForm.reason_code : undefined,
        note: reviewForm.note || undefined,
      });
      setDetails(resp?.data || details);
      setRejectingId(null);
      setReviewForm({ reason_code: '', note: '' });
      onUpdated?.();
      toast.success(decision === 'approved' ? 'Document approved.' : 'Document marked for resubmission.');
    } catch (err) {
      toast.error(err?.message || 'Failed to review document');
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
          <Eye className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Loan File Details</h2>
          <p className="text-sm text-muted-foreground">{resolvedLoan.id}</p>
        </div>
      </div>

      <div className="rounded-xl bg-muted/50 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-lg font-bold">{applicantName}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={resolvedLoan.status} />
              <span className="text-xs text-muted-foreground">{resolvedLoan.stage}</span>
            </div>
          </div>
          {loading ? <span className="text-xs text-muted-foreground">Refreshing...</span> : null}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border p-3 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Loan Type</p>
          <p className="mt-1 font-semibold">{loanType}</p>
        </div>
        <div className="rounded-xl border p-3 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Bank / NBFC</p>
          <p className="mt-1 font-semibold">{bankName}</p>
        </div>
        <div className="rounded-xl border p-3 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Loan Amount</p>
          <p className="mt-1 font-semibold text-emerald-600">{fmt(loanAmount)}</p>
        </div>
        <div className="rounded-xl border p-3 text-sm">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Applied Date</p>
          <p className="mt-1 font-semibold">{appliedDate}</p>
        </div>
      </div>

      {Object.keys(productDetails).length > 0 ? (
        <div className="rounded-xl border p-4 text-sm space-y-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Application Details</p>
          {productDetails.basic?.fullName ? <p><span className="font-medium">Customer:</span> {productDetails.basic.fullName}</p> : null}
          {productDetails.personal?.fullName ? <p><span className="font-medium">Customer:</span> {productDetails.personal.fullName}</p> : null}
          {productDetails.student?.studentName ? <p><span className="font-medium">Student:</span> {productDetails.student.studentName}</p> : null}
          {productDetails.business?.businessName ? <p><span className="font-medium">Business:</span> {productDetails.business.businessName}</p> : null}
          {productDetails.property?.propertyLocation ? <p><span className="font-medium">Property Location:</span> {productDetails.property.propertyLocation}</p> : null}
        </div>
      ) : null}
      {resolvedLoan.remarks ? (
        <div className="rounded-xl border p-4 text-sm space-y-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Remarks</p>
          <p>{resolvedLoan.remarks}</p>
        </div>
      ) : null}

      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Uploaded Documents</p>
        {documents.length === 0 ? (
          <div className="rounded-xl border border-dashed px-4 py-6 text-sm text-muted-foreground">
            No documents uploaded yet.
          </div>
        ) : (
          documents.map((doc) => {
            const isRejected = doc.current_status === 'rejected';
            const isApproved = doc.current_status === 'approved';
            const openReject = rejectingId === doc.id;
            return (
              <div key={doc.id || doc.key} className={`rounded-xl border p-4 space-y-3 ${isRejected ? 'border-rose-300 bg-rose-50/60 dark:bg-rose-950/20' : ''}`}>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-1">
                    <p className="font-semibold text-sm">{doc.document_type || doc.document_label || doc.key}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <StatusBadge status={isRejected ? 'Rejected' : isApproved ? 'Approved' : 'Under Processing'} />
                      <span>Version {doc.version_number || 0}</span>
                      {doc.file_name ? <span>{doc.file_name}</span> : null}
                    </div>
                  </div>
                  {doc.file_url ? (
                    <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                      Open file
                    </a>
                  ) : null}
                </div>

                {isRejected ? (
                  <div className="rounded-lg border border-rose-200 bg-white/70 px-3 py-2 text-xs space-y-1 dark:bg-slate-900/40">
                    <p><span className="font-medium">Reason:</span> {doc.rejection_reason_code || '-'}</p>
                    <p><span className="font-medium">Comment:</span> {doc.rejection_note || '-'}</p>
                    <p><span className="font-medium">Reviewer:</span> {doc.reviewer_name || '-'}
                    {/* {doc.reviewer_role ? ` (${doc.reviewer_role})` : ''} */}
                    </p>
                    <p><span className="font-medium">Reviewed At:</span> {doc.reviewed_at || '-'}</p>
                  </div>
                ) : null}

                {Array.isArray(doc.history) && doc.history.length > 0 ? (
                  <div className="rounded-lg border px-3 py-2 text-xs space-y-2">
                    <p className="font-medium">Version History</p>
                    {doc.history.map((item) => (
                      <div key={item.version_id} className="border-t first:border-t-0 pt-2 first:pt-0">
                        <p>v{item.version_number} - {item.file_name}</p>
                        <p className="text-muted-foreground">Uploaded: {item.uploaded_at || '-'} | Review: {item.review_decision || 'pending'}</p>
                        {item.reason_code ? <p className="text-rose-600">Reason: {item.reason_code}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {canReview ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={reviewingId === doc.id || isApproved}
                      onClick={() => submitReview(doc.id, 'approved')}
                    >
                      {reviewingId === doc.id ? 'Saving...' : 'Approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-100"
                      disabled={reviewingId === doc.id}
                      onClick={() => {
                        setRejectingId(openReject ? null : doc.id);
                        setReviewForm({ reason_code: doc.rejection_reason_code || '', note: doc.rejection_note || '' });
                      }}
                    >
                      Request Resubmission
                    </Button>
                  </div>
                ) : null}

                {canReview && openReject ? (
                  <div className="rounded-lg border border-rose-200 bg-white/80 p-3 space-y-3 dark:bg-slate-900/40">
                    <div className="space-y-1.5">
                      <Label>Reason for rejection</Label>
                      <Select value={reviewForm.reason_code || undefined} onValueChange={(value) => setReviewForm((prev) => ({ ...prev, reason_code: value }))}>
                        <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                        <SelectContent>
                          {reasonOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Comment</Label>
                      <Textarea rows={3} placeholder="Explain what the agent needs to fix" value={reviewForm.note} onChange={(e) => setReviewForm((prev) => ({ ...prev, note: e.target.value }))} />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setRejectingId(null)}>Cancel</Button>
                      <Button
                        size="sm"
                        className="bg-rose-600 text-white hover:bg-rose-700"
                        disabled={reviewingId === doc.id || !reviewForm.reason_code}
                        onClick={() => submitReview(doc.id, 'rejected')}
                      >
                        {reviewingId === doc.id ? 'Saving...' : 'Reject Document'}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {showFooter ? (
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      ) : null}
    </div>
  );
}

export default function LoanReviewModal({ loan, open, onClose, onUpdated }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Loan File Details</DialogTitle>
          <DialogDescription>{loan?.id || ''}</DialogDescription>
        </DialogHeader>
        {open ? (
          <LoanReviewDetails loan={loan} loanId={loan?.id} onUpdated={onUpdated} showFooter onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
